import { z } from 'zod';
import { ApiParams, Method, queryData as fetchData } from 'shared-components';
import {
  configureFetch,
  ApiClient,
  GetActiveIntakesWithCoursesResponse,
  ApiResponse,
  SchedulingInfoResponse,
  DocumentPresignedUrlResponse,
  UpdateOfferRequirementInternalDataResponse,
  DeleteStaffInternalDocumentResponse,
} from 'api-paths';
import {
  AddPartnersBulkActionAPIPayload,
  AddTagsBulkActionAPIPayload,
  ApplicantOfferPGFilterMap,
  ApplicantOfferPaymentStatus,
  ApplicantOfferRequirementTaskStatus,
  SettableTaskLevel,
  RequestableTaskLevel,
  ApplicationPGFilterMap,
  AppliedEUSSStatus,
  AssignImmigrationBulkActionAPIPayload,
  AssignStageBulkActionAPIPayload,
  CourseLevel,
  CreateApplicantOfferRequirementTaskSchema,
  DropdownOptions,
  EUSSStatus,
  ErrorResponseBody,
  GetApplicationsStageTotalsResponse,
  GetApplicationsSummaryResponse,
  GetApplyAuditLogResponse,
  GetOfferTimelineResponse,
  GetApplyConfigResponse,
  GetApplyEmailsResponse,
  GetOffersStageTotalsResponse,
  GetOffersSummaryResponse,
  GetStaffApplicationSummaryResponse,
  IApplicantOffer,
  IApplicantOfferPayment,
  IApplicantOfferRequirementTask,
  IApplicantUpdateOfferPaymentStatus,
  ICalendlyEvent,
  IConsents,
  IFinance,
  IFinanceDocuments,
  IGetAllApplyCourseResponse,
  IHistory,
  IMessage,
  IOffer,
  IPersonalInfo,
  IPreCasInterview,
  IRecordedInterviewAssessmentPayload,
  IRecordedInterviewEntry,
  IRecordedInterviewProcessingStatus,
  IRecordedInterviewUpdateScoresPayload,
  IRegionInterviewConfig,
  IReinstateApplicantOffer,
  IRisk,
  IStaffCreateApplicantOffer,
  IStudentRow,
  IUpdateApplicantOfferLetterText,
  IUpdateApplicantOfferProgrammeDetails,
  IUpdateApplicantOfferRequirementTaskStatus,
  IUpdateApplicantOfferStatus,
  IVisaPrepForm,
  PartnerVisaStatus,
  RemovePartnersBulkActionAPIPayload,
  RemoveTagsBulkActionAPIPayload,
  IApplicantData,
  DocumentType,
  IDocumentFulfilmentData,
  IProgrammeChoice,
  IApplicantOfferSummaryTableRow,
  IApplicationSummaryTableRow,
  IApplyDashboardRowTotals,
  RouteStatusResponse,
  GenericsIntegration,
  APIResponse,
  ResolveDuplicateHandlerResponse,
  ApplicationStatusTotals,
} from 'types';
import {
  ApplyOfferUpdateProps,
  AuditlogResponse,
  IDocuments,
  ICore,
  ICourse,
  IVisa,
  OVisa,
  VisaRefusal,
} from '../types';
import { IGetAllDashboardRowsParams } from '@/store/offers-dashboard/types';
import { getPsqlFilters } from '@/utilities/getPsqlFilters';
import { defaultApplyUniConfig } from 'data';
import _ from 'lodash';
import { ITotal } from '../store/dashboard/types';
import { IRegionConfigsState } from '../store/region_config_dashboard/types';
import { IModal } from '../store/ui/types';
import { EXPORT_PAGE_SIZE, PAGE_SIZE } from '../utilities/constants';
import { getApiUrl } from '../utilities/envVariables';
import { getGlobalConfig } from '../utilities/globalConfig';
import {
  IAgentRegistrationResponse,
  IPartnerRegistrationResponse,
  IUniStaffRegistrationResponse,
} from '@/store/apply/actions';
import { UserRole } from '@/store/auth/types';
import { serialiseDashboardEntiresToDashboardRows } from '@/components/organisms/ApplyDataGrid/table/rows';
import { TableRow } from '@/components/organisms/ApplyDataGrid/table/types';

// Configure the type-safe API client with our fetch implementation
configureFetch({
  fetch: async (method, url, body, options) => {
    try {
      const result = await fetchData({
        method,
        uri: url,
        domain: await getApiUrl(),
        body,
        idToken: options?.idToken,
        retry: options?.retry,
        onRetry: options?.onRetry,
        onReconnected: options?.onReconnected,
        onFailed: options?.onFailed,
      });

      // Handle the response - check if it's an error
      if (result && typeof result === 'object' && 'errorType' in result) {
        return {
          error: {
            message: (result as any).errorMessage || 'Request failed',
            code: (result as any).errorType,
          },
        };
      }

      return { data: result };
    } catch (error: any) {
      return {
        error: {
          message: error?.message || 'Request failed',
          code: error?.code || 'UNKNOWN_ERROR',
        },
      };
    }
  },
});

// Type guard to check if response is an error
export function isErrorResponse(response: unknown): response is ErrorResponseBody {
  return typeof response === 'object' && response !== null && 'error' in response;
}

export const queryData = async <BodyType = unknown, ReturnType = unknown>(
  params: Partial<ApiParams<BodyType>>,
): Promise<ReturnType | ErrorResponseBody> => {
  return fetchData<BodyType, ReturnType>({ ...params, domain: await getApiUrl(), uri: params.uri! });
};

export interface IFilterParams {
  unis?: Array<string>;
  studentNo?: Array<string>;
  givenName?: Array<string>;
  surname?: Array<string>;
  course?: Array<string>;
  faculty?: Array<string>;
  nationality?: Array<string>;
  country?: Array<string>;
  applicationCountry?: Array<string>;
  activityStatus?: Array<string>;
  notStarted?: Array<string>;
  idle?: Array<string>;
  complete?: Array<string>;
  incomplete?: Array<string>;
  fundsMaturity?: Array<string>;
  market?: Array<string>;
  region?: Array<string>;
  visaStatus?: Array<string>;
  confidence?: Array<string>;
  agent?: Array<string>;
  conditionalStatus?: Array<string>;
  courseConditionalStatus?: Array<string>;
  courseCondtionalStatus?: Array<string>;
  evidenceRequirements?: Array<string>;
  customRegion?: Array<string>;
  tag?: Array<string>;
  programmableTag?: Array<string>;
  services?: Array<string>;
  immigrationProviders?: Array<string>;
  interview?: Array<string>;
  recordedInterview?: Array<string>;
  idleFrom?: Array<number>;
  idleTo?: Array<number>;
  dependants?: Array<string>;
  tagsMatch?: Array<string>;
  programmableTagsMatch?: Array<string>;
  shareCode?: Array<string>;
}

export interface IGetAllParams extends IFilterParams {
  abortController: AbortController | false;
  idToken: string;
  stage: string | string[];
  page?: number;
  uni?: string;
  pathway?: string;
  intakeId?: string;
  pathwayVisibility?: string;
  startDate?: string;
  endDate?: string;
  sortCol?: string;
  sortDir?: string;
  search?: string;
  filters?: { [filterKey: string]: string[] };
  unreadMessages?: string;
  agentId?: string;
  officeId?: string;
  currentUserRegionId?: string;
}

export interface IGetFilterParams extends IFilterParams {
  abortController: AbortController | false;
  idToken: string;
  uni?: string;
  pathway?: string;
  intakeId?: string;
  pathwayVisibility?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

const appendFilterParams = (filterParams: IFilterParams, qp: URLSearchParams) => {
  for (const [key, value] of Object.entries(filterParams)) {
    if (value && value instanceof Array) {
      for (const val of value) {
        qp.append(key, val);
      }
    }
  }
};

export const getAllStudents = async (
  params: IGetAllParams,
  isExporting = false,
): Promise<ErrorResponseBody | { students: Array<IStudentRow> }> => {
  const {
    abortController,
    idToken,
    stage,
    page,
    uni,
    pathway,
    intakeId,
    pathwayVisibility,
    startDate,
    endDate,
    sortCol,
    sortDir,
    search,
    unreadMessages,
    ...filterParams
  } = params;

  const qp = new URLSearchParams();

  qp.set('stage', stage as string);

  const getPagination = (page = 1, isExporting: boolean) => {
    qp.set('limit', String(isExporting ? EXPORT_PAGE_SIZE : PAGE_SIZE));
    qp.set('offset', String(Math.max(0, (page - 1) * (isExporting ? EXPORT_PAGE_SIZE : PAGE_SIZE))));
  };

  getPagination(page, isExporting);

  if (uni) qp.set('uni', uni);
  if (pathway) qp.set('pathway', pathway);
  if (intakeId) qp.set('intakeId', intakeId);
  if (pathwayVisibility) qp.set('pathwayVisibility', pathwayVisibility);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (sortCol) qp.set('sort-col', sortCol);
  if (sortDir) qp.set('sort-dir', sortDir);
  if (search && search !== '') qp.set('search', search);
  if (unreadMessages && unreadMessages === 'true') qp.set('message', 'true');

  appendFilterParams(filterParams, qp);

  return queryData<unknown, { students: Array<IStudentRow>; totals: ITotal[] }>({
    domain: await getApiUrl(),
    uri: '/get-students',
    qp,
    idToken,
    abortController,
  });
};

export const getStudentTotals = async (
  params: Omit<IGetAllParams, 'page' | 'sortCol' | 'sortDir'>,
): Promise<ErrorResponseBody | { totals: ITotal[] }> => {
  const {
    abortController,
    idToken,
    stage,
    uni,
    pathway,
    intakeId,
    pathwayVisibility,
    startDate,
    endDate,
    search,
    unreadMessages,
    ...filterParams
  } = params;

  const qp = new URLSearchParams();

  qp.set('stage', stage as string);

  if (uni) qp.set('uni', uni);
  if (pathway) qp.set('pathway', pathway);
  if (intakeId) qp.set('intakeId', intakeId);
  if (pathwayVisibility) qp.set('pathwayVisibility', pathwayVisibility);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (search && search !== '') qp.set('search', search);
  if (unreadMessages && unreadMessages === 'true') qp.set('message', 'true');

  appendFilterParams(filterParams, qp);

  return queryData({ domain: await getApiUrl(), uri: '/get-students/totals', qp, idToken, abortController });
};

export const getAllApplications = async (
  params: IGetAllParams,
  isExporting = false,
): Promise<{ students: Array<IStudentRow> } | ErrorResponseBody> => {
  const {
    abortController,
    idToken,
    stage,
    page,
    uni,
    pathway,
    intakeId,
    pathwayVisibility,
    startDate,
    endDate,
    sortCol,
    sortDir,
    search,
    unreadMessages,
    ...filterParams
  } = params;

  const qp = new URLSearchParams();

  qp.set('stage', stage as string);

  const getPagination = (page = 1, isExporting: boolean) => {
    qp.set('limit', String(isExporting ? EXPORT_PAGE_SIZE : PAGE_SIZE));
    qp.set('offset', String(Math.max(0, (page - 1) * (isExporting ? EXPORT_PAGE_SIZE : PAGE_SIZE))));
  };

  getPagination(page, isExporting);

  if (uni) qp.set('uni', uni);
  if (pathway) qp.set('pathway', pathway);
  if (pathwayVisibility) qp.set('pathwayVisibility', pathwayVisibility);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (sortCol) qp.set('sort-col', sortCol);
  if (sortDir) qp.set('sort-dir', sortDir);
  if (search && search !== '') qp.set('search', search);
  if (unreadMessages && unreadMessages === 'true') qp.set('message', 'true');

  appendFilterParams(filterParams, qp);

  return queryData<unknown, { students: Array<IStudentRow>; totals: ITotal[] }>({
    domain: await getApiUrl(),
    uri: `/applications/${intakeId}`,
    qp,
    idToken,
    abortController,
  });
};

export const getAllFilters = async (params: IGetFilterParams): Promise<ErrorResponseBody | IVisa> => {
  const { abortController, idToken, uni, pathway, intakeId, pathwayVisibility, startDate, endDate, search } = params;

  const qp = new URLSearchParams();

  if (uni) qp.set('uni', uni);
  if (pathway) qp.set('pathway', pathway);
  if (intakeId) qp.set('intakeId', intakeId);
  if (pathwayVisibility) qp.set('pathwayVisibility', pathwayVisibility);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (search && search !== '') qp.set('search', search);

  return queryData<unknown, IVisa>({
    domain: await getApiUrl(),
    uri: '/get-students/filters',
    qp,
    idToken,
    abortController,
  });
};

export const getStudentById = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<IVisa | ErrorResponseBody> => {
  return queryData<unknown, IVisa>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}`,
    idToken,
  });
};

export const getApplicantById = async (
  idToken: string,
  userId: string,
  applicationId: string,
  uni: string,
): Promise<any> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${userId}/application/${applicationId}`,
    idToken,
  });
};

export const getApplicantRouteStatus = async (
  idToken: string,
  uni: string,
  applicantId: string,
  applicationId?: string,
): Promise<RouteStatusResponse | ErrorResponseBody> => {
  const queryParams = applicationId ? `?applicationId=${applicationId}` : '';
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/agent/${uni}/applicant/${applicantId}/status${queryParams}`,
    idToken,
  });
};

export const getApplicantOfferById = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
): Promise<any> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}`,
    idToken,
  });
};
// We'll use the existing getConfig API for getting university config including intakes

export const getApplicantCourses = async (
  idToken: string,
  applicantId: string,
  uni: string,
  applicationId: string,
  intakeId?: string,
  feeStatus?: string,
  role?: UserRole,
): Promise<{ intakeIds: DropdownOptions[]; courses: DropdownOptions[] } | ErrorResponseBody> => {
  const isAgent = [UserRole.AgencyAdmin, UserRole.Agent].includes(role);
  const isUniStaff = [UserRole.Uni, UserRole.UniAdmin, UserRole.UniLite, UserRole.UniSecure, UserRole.Admin].includes(
    role,
  );
  const isPathwayProvider = [UserRole.Partner].includes(role);

  let uri = `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/courses`;

  if (isAgent || isUniStaff || isPathwayProvider) {
    uri = `/apply/agent/${uni}/courses`;
  }

  const qp = new URLSearchParams();

  if (intakeId) {
    qp.set('intakeId', intakeId);
  }
  if (feeStatus) {
    qp.set('feeStatus', feeStatus);
  }

  return queryData({
    domain: await getApiUrl(),
    uri,
    qp,
    idToken,
  });
};

export const getApplicantCourseChange = async (
  idToken: string,
  applicantId: string,
  uni: string,
  applicationId: string,
  intakeId: string,
  courseId: string,
): Promise<{ programme: IApplicantOffer['programme']; requirements: string[] } | ErrorResponseBody> => {
  const uri = `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/courses/change?intakeId=${intakeId}&courseId=${courseId}`;
  return queryData<undefined, { programme: IApplicantOffer['programme']; requirements: string[] }>({
    domain: await getApiUrl(),
    uri,
    idToken,
  });
};
export const updateApplicantProgramme = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  programmeDetails: IUpdateApplicantOfferProgrammeDetails,
): Promise<IApplicantOffer | ErrorResponseBody> => {
  const uri = `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/programme-details`;
  return queryData({
    method: Method.Put,
    domain: await getApiUrl(),
    uri,
    idToken,
    body: programmeDetails,
  });
};
export const createApplicantOffer = async (
  idToken: string,
  applicantId: string,
  uni: string,
  applicationId: string,
  offer: IStaffCreateApplicantOffer,
): Promise<boolean | ErrorResponseBody> => {
  const uri = `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/offers`;
  return queryData({
    method: Method.Post,
    domain: await getApiUrl(),
    uri,
    idToken,
    body: offer,
  });
};

export const updateApplicantOfferStatus = async (
  idToken: string,
  params: ApplyOfferUpdateProps & IUpdateApplicantOfferStatus & { applicationId: string },
): Promise<IApplicantOffer | ErrorResponseBody> => {
  const {
    applicantId,
    uni,
    offerId,
    status,
    conditions,
    assess,
    startText,
    endText,
    statusReason,
    statusInternalNote,
    statusExplanationToApplicant,
    notifyApplicant,
  } = params;
  const body: IUpdateApplicantOfferStatus = {
    status,
    conditions: conditions || [],
    assess: assess || [],
  };
  if (startText) body.startText = startText;
  if (endText) body.endText = endText;
  if (statusReason) {
    body.statusReason = statusReason;
    body.statusInternalNote = statusInternalNote;
    body.statusExplanationToApplicant = statusExplanationToApplicant;
    body.notifyApplicant = notifyApplicant;
  }
  return queryData<IUpdateApplicantOfferStatus, IApplicantOffer | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/status`,
    idToken,
    body,
  });
};

export const updateApplicantOfferPaymentDepositStatus = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  status: ApplicantOfferPaymentStatus,
  statusInternalNote?: string,
): Promise<IApplicantOfferPayment | ErrorResponseBody> => {
  const body: IApplicantUpdateOfferPaymentStatus = { status };
  if (statusInternalNote !== undefined) {
    body.statusInternalNote = statusInternalNote;
  }
  return queryData<IApplicantUpdateOfferPaymentStatus, IApplicantOfferPayment | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/deposit/status`,
    idToken,
    body,
  });
};

export const updateApplicantOfferPaymentProofStatus = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  status: ApplicantOfferPaymentStatus,
  statusInternalNote?: string,
): Promise<IApplicantOfferPayment | ErrorResponseBody> => {
  const body: IApplicantUpdateOfferPaymentStatus = { status };
  if (statusInternalNote !== undefined) {
    body.statusInternalNote = statusInternalNote;
  }
  return queryData<IApplicantUpdateOfferPaymentStatus, IApplicantOfferPayment | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/payment-proof/status`,
    idToken,
    body,
  });
};

export const reinstateApplicantOffer = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
): Promise<IApplicantOffer | ErrorResponseBody> => {
  const body: unknown = {};
  return queryData<IReinstateApplicantOffer, IApplicantOffer | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/reinstate`,
    idToken,
    body,
  });
};

export const updateApplicantOfferLetterText = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  startText?: string,
  endText?: string,
): Promise<IApplicantOffer | ErrorResponseBody> => {
  return queryData<IUpdateApplicantOfferLetterText, IApplicantOffer | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/letter-text`,
    idToken,
    body: { startText, endText },
  });
};

export const updateApplicantRequirementStatus = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  requirementId: string,
  status: ApplicantOfferRequirementTaskStatus,
  noteText?: string,
  conditionText?: string,
): Promise<IApplicantOfferRequirementTask | ErrorResponseBody> => {
  return queryData<IUpdateApplicantOfferRequirementTaskStatus, IApplicantOfferRequirementTask | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/requirements/${encodeURIComponent(requirementId)}/status`,
    idToken,
    body: { status, noteText, conditionText },
  });
};

export const updateApplicantRequirementLevel = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  requirementId: string,
  level: SettableTaskLevel,
): Promise<IApplicantOfferRequirementTask | ErrorResponseBody> => {
  return queryData<{ level: SettableTaskLevel }, IApplicantOfferRequirementTask | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/requirements/${encodeURIComponent(requirementId)}/level`,
    idToken,
    body: { level },
  });
};

export const exemptApplicantRequirement = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  requirementId: string,
  noteText?: string,
): Promise<IApplicantOffer | ErrorResponseBody> => {
  return queryData<{ noteText?: string }, IApplicantOffer | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/requirements/${encodeURIComponent(requirementId)}/exempt`,
    idToken,
    body: { noteText },
  });
};

export const exemptApplicantOfferPayment = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  noteText?: string,
): Promise<IApplicantOfferPayment | ErrorResponseBody> => {
  return queryData<{ noteText?: string }, IApplicantOfferPayment | ErrorResponseBody>({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/payment/exempt`,
    idToken,
    body: { noteText },
  });
};

export const requestTasks = async (
  idToken: string,
  uni: string,
  applicantId: string,
  offerId: string,
  level: RequestableTaskLevel,
): Promise<IApplicantOffer | ErrorResponseBody> => {
  return queryData<{ level: RequestableTaskLevel }, IApplicantOffer | ErrorResponseBody>({
    method: Method.Post,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/request-tasks`,
    idToken,
    body: { level },
  });
};

export const getStudentDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<IVisa | ErrorResponseBody> => {
  return queryData<unknown, IVisa>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/docs`,
    idToken,
  });
};

export const getAuditlog = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<AuditlogResponse | ErrorResponseBody> => {
  return queryData<unknown, AuditlogResponse>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/audit`,
    idToken,
  });
};

export const getStudentRecordedInterviews = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<Partial<IVisa> | ErrorResponseBody> => {
  return queryData<unknown, Partial<IVisa>>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/recorded-interview`,
    idToken,
  });
};

export const getStudentCalendlyEvents = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<Record<string, ICalendlyEvent> | ErrorResponseBody> => {
  return queryData<unknown, Record<string, ICalendlyEvent>>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/calendly-events`,
    idToken,
  });
};

export const getStudentFinDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<Partial<IVisa> | ErrorResponseBody> => {
  return queryData<unknown, Partial<IVisa>>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/fin-docs`,
    idToken,
  });
};
export const getStudentMessages = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<ErrorResponseBody | Partial<IVisa>> => {
  return queryData<unknown, Partial<IVisa>>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/messages`,
    idToken,
  });
};
export const getStudentFollowUpDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<Partial<IVisa> | ErrorResponseBody> => {
  return queryData<unknown, Partial<IVisa>>({
    domain: await getApiUrl(),
    uri: `/get-student/${uni}/${studentNo}/${intakeId}/${offerId}/follow-up-docs`,
    idToken,
  });
};

export const getConfig = async (idToken: string, university: string): Promise<any> => {
  const response = await ApiClient.Config.getUniConfig({
    pathParams: { uni: university },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getFeatureFlags = async (idToken: string): Promise<Record<string, boolean>> => {
  const response = await ApiClient.Config.getFeatureFlags({ options: { idToken } });
  if (response.data) return response.data as Record<string, boolean>;
  throw response.error;
};

export const getActiveIntakesWithCourses = async (
  idToken: string,
  university: string,
  feeStatus?: string,
): Promise<GetActiveIntakesWithCoursesResponse> => {
  const response = await ApiClient.Apply.ActiveIntakes.withCourses({
    pathParams: { uni: university },
    queryParams: feeStatus ? { feeStatus } : undefined,
    options: { idToken },
  });
  if (response.data) return response.data as GetActiveIntakesWithCoursesResponse;
  throw response.error;
};

export const getNavigationUrls = async (idToken: string, university: string): Promise<any> => {
  const response = await ApiClient.Config.getNavigationUrls({
    pathParams: { uni: university },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export async function getUniEventTypes(idToken: string, uni: string, pathway?: string): Promise<any> {
  let response;
  if (pathway) {
    response = await ApiClient.Config.ScheduledInterviews.Pathway.EventTypes.get({
      pathParams: { uni, pathway },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Config.ScheduledInterviews.Uni.EventTypes.get({
      pathParams: { uni },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function getProgrammableTags(idToken: string, uni: string, pathway?: string): Promise<any> {
  let response;
  if (pathway) {
    response = await ApiClient.Config.Pathway.ProgrammableTags.list({
      pathParams: { uni, pathway },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Config.Uni.ProgrammableTags.list({
      pathParams: { uni },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export const getAppliedTagsNum = async (
  idToken: string,
  uni: string,
  programmableTag: any,
  pathway?: string,
): Promise<any> => {
  let response;
  if (pathway) {
    ApiClient.Config.Pathway.ProgrammableTag.getAppliedCount({
      pathParams: { uni, pathway },
      body: programmableTag,
      options: { idToken },
    });
  } else {
    ApiClient.Config.Uni.ProgrammableTag.getAppliedCount({
      pathParams: { uni },
      body: programmableTag,
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
};

export const studentsPinTag = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
  tags: { id: string; isProgrammable: boolean }[],
): Promise<IVisa | ErrorResponseBody> => {
  const response = await ApiClient.Students.BulkChange.Tags.pin({
    pathParams: { uni },
    body: { studentsIds: studentNumbers, tags },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export const studentsUnpinTags = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
  tags: { id: string; isProgrammable: boolean }[],
): Promise<IVisa | ErrorResponseBody> => {
  const response = await ApiClient.Students.BulkChange.Tags.unpin({
    pathParams: { uni },
    body: { studentsIds: studentNumbers, tags },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export const bulkChange = async (idToken: string, studentIDs: string[], value: string, parameter: string) => {
  const response = await ApiClient.Students.bulkChange({
    body: { studentIDs, value, parameter },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const studentsAddImmigrationProvider = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
  provider: string,
): Promise<IVisa | ErrorResponseBody> => {
  const response = await ApiClient.Students.ImmigrationProvider.update({
    pathParams: { uni },
    body: { studentIDs: studentNumbers, immigrationProvider: provider },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export const studentsRemoveImmigrationProvider = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
): Promise<IVisa | ErrorResponseBody> => {
  const response = await ApiClient.Students.ImmigrationProvider.remove({
    pathParams: { uni },
    body: { studentIDs: studentNumbers },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export const studentsAddServices = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
  services: string[],
): Promise<IVisa | ErrorResponseBody> => {
  const response = await ApiClient.Students.Partners.add({
    pathParams: { uni },
    body: { studentIDs: studentNumbers, partners: services },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export const studentsRemoveServices = async (
  idToken: string,
  uni: string,
  studentNumbers: string[],
  services: string[],
): Promise<ErrorResponseBody | IVisa> => {
  const response = await ApiClient.Students.Partners.remove({
    pathParams: { uni },
    body: { studentIDs: studentNumbers, partners: services },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IVisa;
  throw response.error;
};

export async function getAllRegionConfigs(
  idToken: string,
  uni: string,
  pathway?: string,
): Promise<IRegionConfigsState | ErrorResponseBody> {
  let response;
  if (pathway) {
    response = await ApiClient.Config.Pathway.RegionConfigs.get({
      pathParams: { uni, pathway },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Config.Uni.RegionConfigs.get({
      pathParams: { uni },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function getAllFilterRegionConfigs(
  idToken: string,
  uni: string,
  pathway?: string,
): Promise<IRegionConfigsState | ErrorResponseBody> {
  let response;
  if (pathway) {
    response = await ApiClient.Config.Uni.RegionConfigs.getAllPathway({
      pathParams: { uni },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Config.Uni.RegionConfigs.get({
      pathParams: { uni },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export const getAgents = async (idToken: string, uni: string, query: string): Promise<any> => {
  const response = await ApiClient.Agents.search({
    pathParams: { uni, query },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

interface OrganisationSponsorship {
  orgType: string;
  sponsorName: string;
  isMultiCountry?: boolean;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;
}

interface ScholarshipSponsorship {
  scholarshipName: string;
  sponsorshipCovers: Array<string>;
  courseFundType: string;
  courseFundPercentage: number;
  courseFundAmount: number;
  livingFundType: string;
  livingFundPercentage: number;
  livingFundAmountType: string;
  livingFundAmount: number;
  courseAndLivingFundSeparate?: boolean;
  courseAndLivingFundType: string;
  courseAndLivingFundPercentage: number;
  courseAndLivingFundAmount: number;
}

interface EducationSponsorship {
  loanType: string;
  country: string;
  bankName: string;
  loanName: string;
  sponsorshipAmount: number;
  personName: string;
  loanDesc: string;
  personFirstName: string;
  personSurname: string;
  personDesc: string;
}

interface FamilySponsorship {
  familySponsorType: string;
  accountType: string;
  jointType: string;
  accountFirstName: string;
  accountSurname: string;
  otherReason: string;
  country: string;
  bankName: string;
  totalAmount: number;
  sponsorshipAmount: number;
  canWithdrawImmediately: boolean;
}

interface OtherSponsorship {
  otherDesc: string;
  otherName: string;
  sponsorshipAmount: number;
}

export async function createSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: OrganisationSponsorship | ScholarshipSponsorship | EducationSponsorship | FamilySponsorship | OtherSponsorship,
  sponsorType: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.Finance.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { ...body, sponsorType },
    options: { idToken },
  });

  if (response.data) return response.data;
  throw response.error;
}

export async function updateSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorKey: string,
  body: OrganisationSponsorship | ScholarshipSponsorship | EducationSponsorship | FamilySponsorship | OtherSponsorship,
  sponsorType: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.Finance.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { ...body, sponsorType, sponsorKey },
    options: { idToken },
  });

  if (response.data) {
    return response.data;
  }
  throw response.error;
}

export async function removeSponsor(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorKey: string,
  sponsorType: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.Finance.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { sponsorType, sponsorKey, action: 'delete' },
    options: { idToken },
  });

  if (response.data) {
    return response.data;
  }
  throw response.error;
}

export async function removeRegion(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.Finance.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { sponsorType, action: 'delete' },
    options: { idToken },
  });

  if (response.data) {
    return response.data;
  }
  throw response.error;
}

// ####### CAS Interview #########

export async function resetInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.CredibilityInterview.reset({
    pathParams: { uni, studentNo, intakeId, offerId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function getRegionInterviewConfig(
  idToken: string,
  uni: string,
  pathway?: string,
): Promise<IRegionInterviewConfig[]> {
  let response;
  if (pathway) {
    response = await ApiClient.Config.Pathway.InterviewConfigs.get({
      pathParams: { uni, pathway },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Config.Uni.InterviewConfigs.get({
      pathParams: { uni },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export const requestStudentRecordedInterview = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  interviewId: string,
  note: string,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.RecordedInterview.request({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { interviewId, note },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkRequestStudentRecordedInterview = async (
  idToken: string,
  uni: string,
  regionInterviewConfig: IRegionInterviewConfig,
  students?: string[],
  filters?: IGetAllParams,
): Promise<IOffer | ErrorResponseBody> => {
  const response = await ApiClient.Students.BulkChange.RecordedInterview.add({
    pathParams: { uni },
    body: {
      studentIDs: students ?? [],
      filters: filters ?? [],
      data: [regionInterviewConfig],
    },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IOffer;
  throw response.error;
};

export async function cancelRecordedInterviewBulk(
  idToken: string,
  uni: string,
  regionInterviewConfig: IRegionInterviewConfig,
  students?: string[],
  filters?: IGetAllParams,
): Promise<any> {
  const response = await ApiClient.Students.BulkChange.RecordedInterview.remove({
    pathParams: { uni },
    body: {
      studentIDs: students ?? [],
      filters: filters ?? [],
      data: regionInterviewConfig.interviewId,
      note: '',
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function cancelRecordedInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  interviewHash: string,
  note: string,
): Promise<IOffer | ErrorResponseBody> {
  const response = await ApiClient.Student.Offer.RecordedInterview.cancel({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { interviewHash, note },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IOffer;
  throw response.error;
}

export interface I1Update {
  whyUk: string;
  whyUkOther: string;
  selectedCourseReasons: Array<string>;
  selectedCourseReasonsOther: string;
  selectedUniReasons: Array<string>;
  selectedUniReasonsOther: string;
  uniChoiceReason1: string;
  uniChoiceReason2: string;
  uniChoiceReason3: string;
}

export interface I2Update {
  campus: string;
  considerOtherUni?: boolean;
  otherUni1: string;
  otherUni2: string;
  selectedResearchMethods: Array<string>;
  selectedResearchMethodsOther: string;
  whyNotOtherUni: string;
  considerOtherCourses?: boolean;
  considerOtherCourse: string;
  whyNotConsiderOtherCourse: string;
  courseAlignFutureCareer?: boolean;
  courseAlignFutureCareerOther: string;
}

export interface I3Update {
  workWhileStudy?: boolean;
  maxWorkHoursPerWeek: number;
  whyWorkWhileStudy: string;
  whyWorkWhileStudyOther: string;
  affordCourseAndLivingWithoutWorking?: boolean;
  affordCourseAndLivingWithoutWorkingNo: string;
  completeDegreeWorkCountry: string;
  expectedSalary: number;
  furtherStudyInUk?: boolean;
  furtherStudyCareerPlans: string;
}

export interface I4Update {
  liveWhileStudying: string;
  liveWhileStudyingOther: string;
  expectedWeeklyRent: number;
  commutePlan: string;
  expectedLivingExpenses: number;
  awareOfUKVIInterview?: boolean;
}

export async function updateInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: I1Update | I2Update | I3Update | I4Update,
  step: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.CredibilityInterview.update({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { ...body, step },
    options: { idToken },
  });
  if (response.data) {
    return response.data;
  }
  throw response.error;
}

// ####### Visa Prep #########

export async function enableVisaPrep(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.VisaPrep.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { action: 'enable' },
    options: { idToken },
  });
  if (response.data) {
    return response.data;
  }
  throw response.error;
}

export interface V1Update {
  hasAdditionalPassports: boolean;
  additionalPassports: Array<Passport>;
}

export interface Passport {
  docNumber: string;
  placeOfIssue: string;
  issuingAuthority: string;
  issueDate: string;
  expiryDate: string;
}

export interface V12Update {
  residentialAddressStartDate: string;
  homeOwnershipStatus: string;
  homeOwnershipStatusOther: string;
  hasDifferentContactDetails?: boolean;
  diffContactAddress: string;
  diffContactCity: string;
  diffContactPostcode: string;
  diffContactCountry: string;
}

export interface V21Update {
  nonHomeCountryApplicationDesc: string;
}

export interface V22Update {
  hasOtherUkVisas: boolean;
  otherUkVisas: Array<OtherUkVisas>;
}

export interface OtherUkVisas {
  visaType: string;
  issueDate: string;
  expiryDate: string;
  country: string;
  issuingAuthority: string;
}

export interface V23Update {
  tripsToUk: Array<TripsToUK>;
}

export interface TripsToUK {
  arrivalDate: string;
  departureDate: string;
  purpose: string;
}

export interface V24Update {
  hasBeenToUk: boolean;
  hasNINumber?: boolean;
  niNumber: string;
  hasReceivedMedicalTreatment?: boolean;
  hasVoluntarilyDeparted?: boolean;
  hasExclusionOrder?: boolean;
  exclusionOrders: Array<ExclusionOrders>;
}

export interface ExclusionOrders {
  exclusionDate: string;
  reason: string;
  reference: string;
}

export interface V25Update {
  leaveToRemains: Array<LeaveToRemains>;
}

export interface LeaveToRemains {
  applicationDate: string;
  applicationType: string;
  reference: string;
}

export interface V26Update {
  borderRefusals: Array<BorderRefusals>;
}

export interface BorderRefusals {
  refusalDate: string;
  airport: string;
  reason: string;
  reference: string;
}

export interface V27Update {
  hasBeenDeported: boolean;
  deportations: Array<Deportations>;
}

export interface Deportations {
  country: string;
  removalDate: string;
  airport: string;
  reason: string;
}

export interface V28Update {
  hasTravelled: boolean;
  travelItems: Array<TravelItems>;
}

export interface TravelItems {
  country: string;
  reason: string;
  reasonOther: string;
  entryDate: string;
  exitDate: string;
}

export interface V31Update {
  maritalStatus: string;
  spouseNationality: string;
  spouseSameNationality?: boolean;
  spouseFamilyName: string;
  spouseGivenName: string;
  spouseDob: string;
  spouseLivingWith?: boolean;
  spouseTravellingWith: string;
  spouseUkVisa?: boolean;
  spouseHasPassport?: boolean;
  spousePassportNumber: string;
}

export interface V32Update {
  hasFathersPersonalDetails: boolean;
  fatherNationality: string;
  fatherSameNationality?: boolean;
  fatherFamilyName: string;
  fatherGivenName: string;
  fatherDob: string;
  fatherPlaceOfBirth: string;
  fatherTravellingWith: string;
  fatherUkVisa?: boolean;
  fatherHasPassport?: boolean;
  fatherPassportNumber: string;
}

export interface V33Update {
  hasMothersPersonalDetails: boolean;
  motherNationality: string;
  motherSameNationality?: boolean;
  motherFamilyName: string;
  motherGivenName: string;
  motherDob: string;
  motherPlaceOfBirth: string;
  motherTravellingWith: string;
  motherUkVisa?: boolean;
  motherHasPassport?: boolean;
  motherPassportNumber: string;
}

export interface V34Update {
  hasGuardiansPersonalDetails: boolean;
  guardianNationality: string;
  guardianSameNationality?: boolean;
  guardianFamilyName: string;
  guardianGivenName: string;
  guardianDob: string;
  guardianPlaceOfBirth?: string;
  guardianTravellingWith: string;
  guardianUkVisa?: boolean;
  guardianHasPassport?: boolean;
  guardianPassportNumber: string;
}

export interface V35Update {
  hasDependantChildren: boolean;
  children: Array<Children>;
  childrenLivingWithYou?: boolean;
  contactAddress?: string;
  contactCity?: string;
  contactCountry?: string;
  contactPostcode?: string;
}

export interface Children {
  nationality: string;
  sameNationality?: boolean;
  familyName: string;
  givenName: string;
  dob: string;
  placeOfBirth: string;
  travellingWith: string;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
}

export interface V4Update {
  travellingWithOthers: boolean;
  otherTravellers: Array<OtherTravellers>;
}

export interface OtherTravellers {
  relationship: string;
  relationshipOther: string;
  adultOrChild: string;
  nationality: string;
  sameNationality?: boolean;
  familyName: string;
  givenName: string;
  dob: string;
  ukVisa?: boolean;
  hasPassport?: boolean;
  passportNumber: string;
}

export interface Family {
  nationality: string;
  familyName: string;
  givenName: string;
  dob: string;
}

export interface V5Update {
  hasArrivalDate: boolean;
  arrivalDate: string;
  lengthOfStayDays: number;
  lengthOfStayMonths: number;
  lengthOfStayYears: number;
  ukPresence: boolean;
  ukFamily: boolean;
  stayingWithFamily: boolean;
  family: Array<Family>;
  familyContactAddress: string;
  familyContactCity: string;
  familyContactCountry: string;
  familyContactPostcode: string;
  familyPhoneNumber: string;
  familyEmail: string;
  hasOtherContactAddress: boolean;
  otherContactAddress: string;
  otherContactCity: string;
  otherContactCountry: string;
  otherContactPostcode: string;
  otherPhoneNumber: string;
}

export interface V6Update {
  hasCriminalCharges: boolean;
  hasTerrorismInvolvement: boolean;
  hasTerrorismConnection: boolean;
  hasTerrorismJustified: boolean;
  hasWarCrimes: boolean;
  hasBadActivities: boolean;
  language: string;
}

export interface V71Update {
  hasReceivedCas: boolean;
  studiesConfirmationNumber: string;
  courseEndDate: string;
  alreadyStartedStudying: boolean;
  studyAddress: string;
  assessedBySponsor: boolean;
}

export interface V72Update {
  qualificationAsProofOfEnglish: boolean;
  qualificationAndCountry: string;
}

interface V73Update {
  appliedThroughUCAS: boolean;
  ucasPin: string;
}

interface V8Update {
  otherFeedback: string;
  difficultiesFeedback: string;
}

export async function updateVisaPrep(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body:
    | V1Update
    | V12Update
    | V21Update
    | V22Update
    | V23Update
    | V24Update
    | V25Update
    | V26Update
    | V27Update
    | V28Update
    | V31Update
    | V32Update
    | V33Update
    | V34Update
    | V35Update
    | V4Update
    | V5Update
    | V6Update
    | V71Update
    | V72Update
    | V73Update
    | V8Update,
  step: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.VisaPrep.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { ...body, step },
    options: { idToken },
  });
  if (response.data) {
    return response.data;
  }
  throw response.error;
}

export interface OtherName {
  surname: string;
  givenName: string;
  reason: string;
}

export interface PIP1Update {
  givenName?: string;
  surname?: string;
  middleName?: string;
  hasOtherNames?: boolean;
  otherNames?: Array<OtherName>;
}

export interface OtherNationality {
  nationality: string;
  current: boolean;
}

export interface PIP2Update {
  gender?: string;
  dob?: string;
  birthCountry?: string;
  nationality?: string;
  appliedForEUSS?: AppliedEUSSStatus;
  grantedEUSSStatus?: EUSSStatus;
  applyingFromHomeCountry?: boolean;
  countryOfVisaApplication?: string;
  livedInUKLast12Months?: boolean;
  otherCountryReason?: string;
  hasOtherNationality?: boolean;
  otherNationalities?: Array<OtherNationality>;
}

export interface PIP3Update {
  phoneNumber?: string;
  homeAddress?: string;
  city?: string;
  postCode?: string;
  country?: string;
}

export interface PIP4Update {
  hasCriminalConvictions?: boolean;
  criminalConvictionReason?: string;
}

export interface PIP5Update {
  studentNo?: string;
  courses: Array<ICourse>;
  casReceived?: boolean;
}

export interface PIP5_1Update {
  courses: Array<ICourse>;
}

export interface PIP6Update {
  hasEnglishTest?: boolean;
  englishTestType?: string;
  englishTestTypeOther?: string;
  englishTestNotReqdReason?: string;
}

export interface PIP7Update {
  passportStatus?: string;
  passportNumber?: string;
  passportExpectedDate?: string;
  passportExpiryDate?: string;
}

export interface PIP8Update {
  withAgent: string;
}

export interface PIP9Update {
  visaSupport: boolean;
}

export interface PIP10Update {
  useWhatsapp: string;
  whatsAppPhoneNumber: string;
}

export interface StudyVisa {
  id?: string;
  visaType: string;
  level: string;
  year: string;
  isComplete?: boolean;
  incompleteReason?: string;
  isIncompleteRecorded?: boolean;
  isVisaShortened?: boolean;
  shortenedDate?: string;
}

export interface H1Update {
  lastCourse?: string;
  lastCourseOther?: string;
  lastCourseYear?: string;
  hasOverseasTravel?: boolean;
  overseasTravelTimes?: string;
  hasUkTravel?: boolean;
  ukTravelTimes?: string;
  hasPreviousUkStudy?: boolean;
  hasPreviousUkStudyVisa?: boolean;
  previousUkStudyVisas?: Array<StudyVisa>;
  lastCourseLevel?: CourseLevel;
  lowerOrSameLevelReason?: string;
}

export interface H2Update {
  hasOtherUkVisas?: boolean;
  otherUkVisas?: Array<OVisa>;

  hasLeaveToRemain?: boolean;
  hasBorderRefusal?: boolean;
  borderRefusalReason?: string;
  hasUkVisaRefusals?: boolean;
  ukVisaRefusals?: Array<OVisa>;
  hasOtherVisaRefusals?: boolean;
  otherVisaRefusals?: Array<VisaRefusal>;
}

export interface H3Update {
  hasDependents?: boolean;
  hasPartner?: boolean;
  partnerVisaStatus?: PartnerVisaStatus;
  hasChildren?: boolean;
  childrenNum?: number;
}

interface FDeposit {
  courseFeeDepositReqd: string;
  courseFeeDeposit: number;
  courseFeeDepositDate: string;
  courseFeeDepositNotReqdReason: string;
  accomDepositReqd: string;
  accomDeposit: number;
  accomDepositDate: string;
}

export async function updateStudent(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body:
    | PIP1Update
    | PIP2Update
    | PIP3Update
    | PIP4Update
    | PIP5Update
    | PIP5_1Update
    | PIP6Update
    | PIP7Update
    | PIP8Update
    | PIP9Update
    | PIP10Update
    | H1Update
    | H2Update
    | H3Update
    | FDeposit
    | { response: 'approve' | 'reject' },
  step: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { ...body, step },
    options: { idToken },
  });

  if (response.data) {
    return response.data;
  }
  throw response.error;
}

export const saveMessage = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  message: IMessage,
): Promise<IMessage> => {
  const response = await ApiClient.Student.Messages.send({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      studentID: studentNo,
      message: {
        ...message,
        isMessageError: false,
        isMessageSaving: false,
      },
    },
    options: { idToken },
  });
  if (response.error) {
    throw response.error;
  }
  return response.data as IMessage;
};

export const getUploadUrl = async (
  idToken: string,
  university: string,
  studentID: string,
  uploadConf: {
    openModal: IModal;
    fileName: string;
    fileType: string;
  },
): Promise<string> => {
  console.log('Getting upload URL for', { university, studentID, uploadConf });
  console.log('Upload config:', { ...uploadConf });
  const response = await ApiClient.Upload.getPresignedUrl({
    queryParams: { university, studentID },
    body: {
      university,
      id: studentID,
      ...uploadConf.openModal,
      fileName: uploadConf.fileName,
      fileType: uploadConf.fileType,
    },
    options: { idToken },
  });
  console.log('Received upload URL response', response);
  if (response.data) return response.data;
  throw response.error;
};

export const uploadFile = async (file: any, signedRequest: string) => {
  const options = {
    method: 'PUT',
    body: file.slice(),
  };

  const response = await fetch(signedRequest, options);

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response;
};

export const getApplicantUploadUrl = async (
  idToken: string,
  university: string,
  applicantId: string,
  uploadConf: {
    fileName: string;
    fileType: string;
    docType: string;
    docKey: string | null;
  },
): Promise<{ presignedUrl: string; s3Key: string; fileName: string } | { error: string }> => {
  const url = await fetch(`${await getApiUrl()}/documents/${university}/applicant/${applicantId}/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
      'Cas-Shield-Site': window.location.origin,
    },
    body: JSON.stringify({
      fileName: uploadConf.fileName,
      fileType: uploadConf.fileType,
      docType: uploadConf.docType,
      docKey: uploadConf.docKey,
    }),
  });

  return await url.json();
};

function uploadFileToPresignedUrl({
  presignedUrl,
  s3Key,
  fileName,
  file,
  onProgress,
  signal,
}: {
  presignedUrl: string;
  s3Key: string;
  fileName: string;
  file: Blob;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
}): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const locationHeader = xhr.getResponseHeader('Location');
        const actualFileUrl = locationHeader || presignedUrl.split('?')[0].replace(/([^:]\/)\/+/g, '$1');
        resolve({ url: actualFileUrl, s3Key, fileName });
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    // Handle abort
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    // Handle abort signal
    if (signal?.aborted) {
      reject(new Error('Upload cancelled'));
      return;
    }

    signal?.addEventListener('abort', () => {
      xhr.abort();
    });

    // Configure and send request
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

export const uploadApplicantFileWithProgress = async ({
  file,
  idToken,
  university,
  applicantId,
  uploadConf,
  onProgress,
  signal,
}: UploadFnParams): Promise<UploadResult> => {
  const uploadUrlResponse = await getApplicantUploadUrl(idToken, university, applicantId, uploadConf);

  if ('error' in uploadUrlResponse) {
    throw new Error(uploadUrlResponse.error || 'Failed to get upload URL');
  }

  const { presignedUrl, s3Key, fileName } = uploadUrlResponse;
  return uploadFileToPresignedUrl({ presignedUrl, s3Key, fileName, file, onProgress, signal });
};

export async function getStudentUserRecord(
  idToken: string,
  studentId: string,
  studentEmail: string,
  pathway?: string,
): Promise<any> {
  const [uni] = studentId.split('#');

  let response;
  if (pathway) {
    response = await ApiClient.Users.Pathway.Students.list({
      pathParams: { uni, pathway },
      queryParams: { email: studentEmail },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Users.Uni.Students.list({
      pathParams: { uni },
      queryParams: { email: studentEmail },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function getUserStudents(
  idToken: string,
  uni: string,
  pathway?: string,
  paginationToken?: string,
): Promise<any> {
  let response;
  if (pathway) {
    response = await ApiClient.Users.Pathway.Students.list({
      pathParams: { uni, pathway },
      queryParams: { paginationToken },
      options: { idToken },
    });
  } else {
    response = await ApiClient.Users.Uni.Students.list({
      pathParams: { uni },
      queryParams: { paginationToken },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

// TODO: remove mocking
export async function getApplicantOffers(
  idToken: string,
  uni: string,
  applicantId: string,
): Promise<ErrorResponseBody | any> {
  const uri = `/apply/${uni}/applicant/${applicantId}/application/${applicantId}/summary?mock=true`;
  const data = await queryData<unknown, GetStaffApplicationSummaryResponse>({
    domain: await getApiUrl(),
    uri,
    idToken,
  });
  if (isErrorResponse(data)) {
    throw new Error(typeof data.error === 'string' ? data.error : 'An error occurred');
  }
  return data.offers;
}

export async function reinviteUser(idToken: string, uni: string, username: string, pathway?: string): Promise<any> {
  const userId = username;
  let response;
  if (pathway) {
    response = await ApiClient.User.Pathway.Student.reinvite({
      pathParams: { uni, pathway, userId },
      options: { idToken },
    });
  } else {
    response = await ApiClient.User.Uni.Student.reinvite({
      pathParams: { uni, userId },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function resetPassword(idToken: string, uni: string, username: string, pathway?: string): Promise<any> {
  const userId = username;
  let response;
  if (pathway) {
    response = await ApiClient.User.Pathway.resetPassword({
      pathParams: { uni, pathway, userId, userType: 'students' },
      options: { idToken },
    });
  } else {
    response = await ApiClient.User.Uni.resetPassword({
      pathParams: { uni, userId, userType: 'students' },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function resetUniUserMfa(
  idToken: string,
  username: string,
  university: string,
  pathway?: string,
): Promise<any> {
  const response = await ApiClient.Auth.MFA.reset({
    body: {
      clientId: (await getGlobalConfig()).staffAppClient,
      university,
      username,
      pathway,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function acceptTerms(idToken: string, username: string): Promise<any> {
  const response = await ApiClient.Users.Agent.acceptTerms({
    pathParams: { userId: username },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export const getUniUser = async (idToken: string, uni: string, userId: string): Promise<any> => {
  const response = await ApiClient.User.getStaffUser({
    pathParams: { uni, userId, userType: 'staff' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getImmigrationProviderUser = async (idToken: string, username: string): Promise<any> => {
  const response = await ApiClient.User.getUser({
    pathParams: { userId: username, userType: 'immigration-provider' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getPartnerUser = async (idToken: string, username: string): Promise<any> => {
  const response = await ApiClient.User.getUser({
    pathParams: { userId: username, userType: 'partner' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getAgentUser = async (idToken: string, username: string): Promise<any> => {
  const response = await ApiClient.User.getUser({
    pathParams: { userId: username, userType: 'agents' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getEnrolyUser = async (idToken: string, username: string): Promise<any> => {
  const response = await ApiClient.User.getUser({
    pathParams: { userId: username, userType: 'enroly' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getAdminUser = async (idToken: string, username: string): Promise<any> => {
  const response = await ApiClient.User.getUser({
    pathParams: { userId: username, userType: 'admin' },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const deleteUser = async (idToken: string, uni: string, userId: string): Promise<any> => {
  const response = await ApiClient.User.Uni.delete({
    pathParams: { uni, userId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getAgencies = async (idToken: any): Promise<any> => {
  const response = await ApiClient.Agents.get({
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const getAgency = async (idToken: string, id: string, hasApplyCheck?: boolean): Promise<any> => {
  const response = await ApiClient.Agent.get({
    pathParams: { id },
    queryParams: { hasApplyAccessCheck: hasApplyCheck ? 'true' : 'false' },
    options: { idToken },
  });

  if (response.data) return response.data;
  throw response.error;
};

export const getAgencyAliases = async (idToken: string, uni: string): Promise<any> => {
  const response = await ApiClient.AgentAliases.search({
    pathParams: { uni },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export async function updateVisaSupport(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  action: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.visaSupport({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { action },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export const toggleScheduledInterviewStatusBulk = async (
  idToken: string,
  uni: string,
  body: any,
  status: string,
): Promise<any> => {
  let response;
  if (status === 'add') {
    response = await ApiClient.Students.BulkChange.CalendlyInterview.add({
      pathParams: { uni },
      body,
      options: { idToken },
    });
  }
  if (status === 'remove') {
    response = await ApiClient.Students.BulkChange.CalendlyInterview.remove({
      pathParams: { uni },
      body,
      options: { idToken },
    });
  }
  if (!response) throw new Error('Bad Request');
  if (response.data) return response.data;
  throw response.error;
};

export const toggleScheduledInterviewStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  body: any,
  status: string,
): Promise<any> => {
  let response;
  switch (status) {
    case 'pass': {
      response = await ApiClient.Student.Offer.CalendlyInterview.pass({
        pathParams: { uni, studentNo },
        body,
        options: { idToken },
      });
      break;
    }
    case 'resit': {
      response = await ApiClient.Student.Offer.CalendlyInterview.resit({
        pathParams: { uni, studentNo },
        body,
        options: { idToken },
      });
      break;
    }
    case 'fail': {
      response = await ApiClient.Student.Offer.CalendlyInterview.fail({
        pathParams: { uni, studentNo },
        body,
        options: { idToken },
      });
      break;
    }
    case 'request': {
      response = await ApiClient.Student.Offer.CalendlyInterview.request({
        pathParams: { uni, studentNo },
        body,
        options: { idToken },
      });
      break;
    }
    case 'cancel': {
      response = await ApiClient.Student.Offer.CalendlyInterview.cancel({
        pathParams: { uni, studentNo },
        body,
        options: { idToken },
      });
      break;
    }
  }
  if (response.data) return response.data;
  throw response.error;
};

export const saveConfig = async (idToken: string, uni: string, config: any): Promise<any> => {
  const response = await ApiClient.Config.updateUniConfig({
    pathParams: { uni },
    body: config,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateSFTPKey = async (idToken: string, uni: string, pubKey: string): Promise<any> => {
  const response = await ApiClient.Config.SFTP.updateSSHKey({
    pathParams: { uni },
    body: {
      sshKey: pubKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const saveCalendlyConfig = async (idToken: string, uni: string, calendlyConfig: any): Promise<any> => {
  const response = await ApiClient.Config.ScheduledInterviews.Uni.Config.update({
    pathParams: { uni },
    body: calendlyConfig,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkPinTags = async (idToken: string, uni: string, body: AddTagsBulkActionAPIPayload) => {
  const response = await ApiClient.Students.BulkChange.Tags.add({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};
export const bulkRemoveTags = async (idToken: string, uni: string, body: RemoveTagsBulkActionAPIPayload) => {
  const response = await ApiClient.Students.BulkChange.Tags.remove({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkAssignStage = async (idToken: string, uni: string, body: AssignStageBulkActionAPIPayload) => {
  const response = await ApiClient.Students.BulkChange.Stage.assign({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkAddPartners = async (idToken: string, uni: string, body: AddPartnersBulkActionAPIPayload) => {
  const response = await ApiClient.Students.BulkChange.Partners.add({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkRemovePartners = async (idToken: string, uni: string, body: RemovePartnersBulkActionAPIPayload) => {
  const response = await ApiClient.Students.BulkChange.Partners.remove({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const bulkAssignImmigrationProvider = async (
  idToken: string,
  uni: string,
  body: AssignImmigrationBulkActionAPIPayload,
) => {
  const response = await ApiClient.Students.BulkChange.ImmigrationProvider.assign({
    pathParams: { uni },
    body,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export async function getPartner(idToken: string, partner: string): Promise<any> {
  const response = await ApiClient.System.Partner.get({
    pathParams: { partner },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export const getUniqueCount = async (url: string, params: IGetAllParams): Promise<{ [key: string]: number }> => {
  const {
    abortController,
    idToken,
    stage,
    uni,
    pathway,
    intakeId,
    pathwayVisibility,
    startDate,
    endDate,
    search,
    ...filterParams
  } = params;

  const qp = new URLSearchParams();

  qp.set('stage', stage as string);

  if (uni) qp.set('uni', uni);
  if (pathway) qp.set('pathway', pathway);
  if (intakeId) qp.set('intakeId', intakeId);
  if (pathwayVisibility) qp.set('pathwayVisibility', pathwayVisibility);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (search) qp.set('search', search);

  appendFilterParams(filterParams, qp);

  const data = await queryData<unknown, { [key: string]: number }>({
    domain: await getApiUrl(),
    uri: `/unique-count/${url}`,
    qp,
    idToken,
    abortController,
  });
  if (isErrorResponse(data)) {
    throw new Error(typeof data.error === 'string' ? data.error : 'An error occurred');
  }
  return data;
};

export interface StudentUpdate {
  core?: Partial<ICore>;
  personalInfo?: Partial<IPersonalInfo>;
  history?: Partial<IHistory>;
  finance?: Partial<IFinance>;
  consents?: Partial<IConsents>;
  documents?: Partial<IDocuments>;
  finDocuments?: Partial<IFinanceDocuments>;
  preCAS?: Partial<IPreCasInterview>;
  risks?: Partial<IRisk>;
  visaPrep?: Partial<IVisaPrepForm>;
}

export const triggerSITSTransfer = async (idToken: string, uni: string, studentNumber: string) => {
  return queryData({
    uri: `/sits-transfer`,
    idToken,
    method: 'POST',
    body: {
      uni,
      studentNumbers: [studentNumber],
    },
  });
};

export const triggerScoresRegeneration = async (
  idToken: string,
  uni: string,
  studentNo: string,
  interviewId: string,
) => {
  const response = await ApiClient.Student.RecordedInterview.regenerateScores({
    pathParams: { uni, studentNo, interviewId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const requestAttachmentsPacking = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  includeHidden: boolean = false,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.DownloadAll.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { includeHidden },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};
export const getAttachmentsUrl = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.DownloadAll.get({
    pathParams: { uni, studentNo, intakeId, offerId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const saveRisks = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  source: string,
  risks: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.Risks.put({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      clearedRisks: risks,
      riskGroup: source,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const saveDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Document.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const DeleteDocsDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Document.delete({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const saveFollowUpDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FollowUpDocument.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const deleteFollowUpDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FollowUpDocument.delete({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const saveFinanceDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FinanceDocument.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const deleteFinanceDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FinanceDocument.delete({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const toggleDocStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  reason: string,
  status: string | null,
  isCustomDocRequested?: boolean,
): Promise<any> => {
  const response = await ApiClient.Student.Document.updateStatus({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      status,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
      reason,
      isCustomDocRequested,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const toggleFinDocStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  reason: string,
  status: string | null,
  isCustomDocRequested?: boolean,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FinanceDocument.updateStatus({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      status,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
      reason,
      isCustomDocRequested,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  return response.error;
};

export const toggleFollowUpDocStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string | null,
  docType: string | null,
  docKey: string | null,
  reason: string,
  status: string | null,
  isCustomDocRequested?: boolean,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.FollowUpDocument.updateStatus({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      status,
      docType,
      docKey,
      sponsorType,
      sponsorKey,
      reason,
      isCustomDocRequested,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  return response.error;
};

export async function markMessagesRead(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  messageIds: Array<string>,
): Promise<any> {
  const response = await ApiClient.Student.Messages.read({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { messageIds },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function markMessageUnread(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  messageId: string,
): Promise<any> {
  const response = await ApiClient.Student.Messages.unread({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { messageId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export const requestCustomDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: any,
  isFinDoc?: boolean,
  isFollowUpDoc?: boolean,
  followUpArea?: string,
): Promise<IDocuments | IFinanceDocuments> => {
  let response;
  if (isFollowUpDoc) {
    response = await ApiClient.Student.Offer.FollowUpDocument.requestCustom({
      pathParams: { uni, studentNo, intakeId, offerId },
      body: followUpArea ? { ...body, sponsorType: followUpArea } : { ...body },
      options: { idToken },
    });
  } else if (isFinDoc) {
    response = await ApiClient.Student.Offer.FinanceDocument.requestCustom({
      pathParams: { uni, studentNo, intakeId, offerId },
      body,
      options: { idToken },
    });
  } else {
    response = await ApiClient.Student.Document.requestCustom({
      pathParams: { uni, studentNo, intakeId, offerId },
      body,
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
};

export async function updateVisaRefusalNoDoc(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  sponsorType: string,
  sponsorKey: string,
  docKey: string,
  body: any,
): Promise<any> {
  const response = await ApiClient.Student.Document.noDoc({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType: 'visa',
      docKey,
      sponsorType,
      sponsorKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function updateTBTestNoDoc(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  docKey: string,
  body: any,
): Promise<any> {
  const response = await ApiClient.Student.Document.noDoc({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      docType: 'tbTest',
      docKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export const updateConflict = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  {
    id,
    option,
    index,
  }: {
    id: string;
    option: string;
    index?: number;
  },
): Promise<any> => {
  const response = await ApiClient.Student.Offer.Conflict.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { id, option, index },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const overrideMaturityDate = async (
  _idToken: string,
  _uni: string,
  _studentNo: string,
  _intakeId: string,
  _offerId: string,
  _payload: any,
): Promise<any> => {
  throw new Error('Not Implemented!');
};

export const assignAgents = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  payload: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.Agents.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: payload,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateUniStudentNumber = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  payload: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.updateUniStudentNo({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      uniPartnerId: payload,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateOfferEvidence = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  index: number,
  value: string,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.OfferEvidence.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      value,
      index,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  payload: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.updateStatus({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      status: payload,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateVisaStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  payload: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.VisaStatus.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { status: payload },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const updateArrivalStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  payload: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.TravelStatus.update({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: payload,
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export async function deleteStudent(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
): Promise<any> {
  const response = await ApiClient.Student.Offer.delete({
    pathParams: { uni, studentNo, intakeId, offerId },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function changeStudentNo(
  idToken: string,
  uni: string,
  studentNo: string,
  newStudentNo: string,
  intakeId: string,
  offerId: string,
): Promise<any> {
  const response = await ApiClient.Student.changeStudentNo({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: { studentNo: newStudentNo },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function markStudentActive(
  idToken: string,
  uni: string,
  studentNo: string,
  username: string,
  pathway?: string,
): Promise<any> {
  let response;
  if (pathway) {
    response = await ApiClient.User.Pathway.updateUser({
      pathParams: { uni, pathway, userType: 'students', userId: username },
      body: { studentNo },
      options: { idToken },
    });
  } else {
    response = await ApiClient.User.Uni.updateUser({
      pathParams: { uni, userType: 'students', userId: username },
      body: { studentNo },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export async function removeImportHash(idToken: null | string, uni: string, studentNo: string): Promise<any> {
  const response = await ApiClient.Student.importHashClear({
    pathParams: { uni, studentNo },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
}

export async function revokeStudentAccess(
  idToken: string,
  uni: string,
  username: string,
  pathway?: string,
): Promise<any> {
  let response;
  if (pathway) {
    response = await ApiClient.User.Pathway.delete({
      pathParams: { uni, pathway, userId: username },
      options: { idToken },
    });
  } else {
    response = await ApiClient.User.Uni.delete({
      pathParams: { uni, userId: username },
      options: { idToken },
    });
  }
  if (response.data) return response.data;
  throw response.error;
}

export const saveInterviewDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  eventName: string,
  interviewType: string,
  eventType: string,
  eventKey: string,
  fileNames: string[],
): Promise<any> => {
  const response = await ApiClient.Student.Offer.CalendlyInterview.Doc.post({
    pathParams: { uni, studentNo },
    body: {
      eventName,
      interviewType,
      eventType,
      eventKey,
      fileNames,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const deleteInterviewDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  interviewType: string,
  eventType: string | null,
  eventKey: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.CalendlyInterview.Doc.delete({
    pathParams: { uni, studentNo },
    body: {
      ...body,
      interviewType,
      eventType,
      eventKey,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const toggleRecordedInterviewStatus = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  body: any,
  status: string,
): Promise<any> => {
  let response;
  switch (status) {
    case 'request': {
      response = await ApiClient.Student.Offer.RecordedInterview.request({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
    case 'pass': {
      response = await ApiClient.Student.Offer.RecordedInterview.pass({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
    case 'resit': {
      response = await ApiClient.Student.Offer.RecordedInterview.resit({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
    case 'fail': {
      response = await ApiClient.Student.Offer.RecordedInterview.fail({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
    case 'cancel': {
      response = await ApiClient.Student.Offer.RecordedInterview.cancel({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
    case 'exceeded': {
      response = await ApiClient.Student.Offer.RecordedInterview.exceeded({
        pathParams: { uni, studentNo, intakeId, offerId },
        body,
        options: { idToken },
      });
      break;
    }
  }
  if (response.data) return response.data;
  throw response.error;
};

export const saveRecordedInterviewDocs = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  interviewHash: string,
  attemptHash: string,
  fileNames: string[],
): Promise<any> => {
  const response = await ApiClient.Student.Offer.RecordedInterview.post({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      interviewHash,
      attemptHash,
      fileNames,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export const deleteRecordedInterviewDocument = async (
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  interviewType: string,
  interviewHash: string | null,
  attemptHash: string | null,
  body: any,
): Promise<any> => {
  const response = await ApiClient.Student.Offer.RecordedInterview.delete({
    pathParams: { uni, studentNo, intakeId, offerId },
    body: {
      ...body,
      interviewType,
      interviewHash,
      attemptHash,
    },
    options: { idToken },
  });
  if (response.data) return response.data;
  throw response.error;
};

export async function assesssRecordedInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  interviewId: string,
  payload: IRecordedInterviewAssessmentPayload,
): Promise<Record<string, IRecordedInterviewEntry>> {
  const response = await ApiClient.Student.RecordedInterview.assessment({
    pathParams: { uni, studentNo, interviewId },
    body: payload,
    options: { idToken },
  });
  if (response.data) return response.data as unknown as Record<string, IRecordedInterviewEntry>;
  throw response.error;
}

export async function editQuestionScoreRecordedInterview(
  idToken: string,
  uni: string,
  studentNo: string,
  interviewId: string,
  payload: IRecordedInterviewUpdateScoresPayload,
): Promise<Record<string, IRecordedInterviewEntry>> {
  const response = await ApiClient.Student.RecordedInterview.editScores({
    pathParams: { uni, studentNo, interviewId },
    body: payload,
    options: { idToken },
  });
  if (response.data) return response.data as unknown as Record<string, IRecordedInterviewEntry>;
  throw response.error;
}

export async function getVideoProcessingStatus(
  idToken: string,
  uni: string,
  studentNo: string,
  interviewId: string,
  videoId: string,
): Promise<ErrorResponseBody | IRecordedInterviewProcessingStatus> {
  const response = await ApiClient.Student.RecordedInterview.processing({
    pathParams: { uni, studentNo, interviewId, videoId },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as IRecordedInterviewProcessingStatus;
  throw response.error;
}

export async function getInterviewImages(
  idToken: string,
  uni: string,
  studentNo: string,
  intakeId: string,
  offerId: string,
  videoId: string,
): Promise<{ captured: string[]; passport: string }> {
  const response = await ApiClient.Student.Offer.interviewImages({
    pathParams: { uni, studentNo, intakeId, offerId },
    queryParams: { collectionId: videoId },
    options: { idToken },
  });
  if (response.data) return response.data as unknown as { captured: string[]; passport: string };
  throw response.error;
}

export const getApplyAuditLog = async (
  idToken: string,
  uni: string,
  applicantId: string,
  applicationId: string,
): Promise<GetApplyAuditLogResponse | ErrorResponseBody> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/audit-log`,
    idToken,
  });
};

export const getOfferTimeline = async (
  idToken: any,
  uni: string,
  applicantId: string,
  applicationId: string,
  offerId: string,
): Promise<GetOfferTimelineResponse | ErrorResponseBody> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/offer/${offerId}/offer-timeline`,
    idToken,
  });
};

export const getApplyEmails = async (
  idToken: string,
  uni: string,
  applicantId: string,
): Promise<GetApplyEmailsResponse | ErrorResponseBody> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/emails`,
    idToken,
  });
};

export const saveAdditionalRequirements = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  payload: z.infer<typeof CreateApplicantOfferRequirementTaskSchema>,
): Promise<GetApplyEmailsResponse | ErrorResponseBody> => {
  console.log(`params ${uni} ${applicantId} ${offerId} ${payload}`);
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/requirements`,
    method: Method.Post,
    idToken,
    body: payload,
  });
};

export const selectStudiesToAssess = async (
  idToken: string,
  applicantId: string,
  uni: string,
  offerId: string,
  qualificationEntryId: string,
  studyIds: string[],
): Promise<unknown> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/requirements/academic/studies`,
    method: Method.Put,
    idToken,
    body: { qualificationEntryId, studyIds },
  });
};

/**
 * Assigns a student number to an applicant if its a duplicate, otherwise marks the applicant as not a duplicate
 * This function is used when an applicant record is identified as a duplicate
 * of an existing student
 */
export async function updateDuplicate({
  idToken,
  integration,
  uni,
  userId,
  applicationId,
  isDuplicate,
  studentNo,
}: {
  idToken: string;
  integration: GenericsIntegration.Integrations | '';
  uni: string;
  userId: string;
  applicationId: string;
  isDuplicate: boolean;
  studentNo?: string;
}): Promise<ApiResponse<APIResponse<ResolveDuplicateHandlerResponse>>> {
  if (!integration) {
    throw new Error('Unsupported integration');
  }

  return await ApiClient.Integration.Applicants.ResolveDuplicate.post({
    pathParams: {
      integration,
    },
    body: {
      uni,
      userId,
      applicationId,
      isDuplicate,
      studentNo,
    },
    options: {
      idToken,
    },
  });
}

export async function getAllDashboardRows(params: IGetAllDashboardRowsParams): Promise<TableRow[]> {
  const offers = await queryDatabaseForApplicantOffers(params, params.offerStages);
  const applications = await queryDatabaseForApplications(params, params.appStages);

  if (!offers && !applications) {
    console.warn('No offers or applications found for dashboard rows');
    return [];
  }

  const offerRows = serialiseDashboardEntiresToDashboardRows(offers);
  const applicationRows = serialiseDashboardEntiresToDashboardRows(applications);
  const combinedRows = [...offerRows, ...applicationRows];

  return combinedRows;
}

export interface IGetAllDashboardRowsTotalsResponse {
  totals: IApplyDashboardRowTotals;
}

export async function getAllDashboardRowTotals(
  params: Partial<IGetAllDashboardRowsParams>,
): Promise<IGetAllDashboardRowsTotalsResponse> {
  const offerTotals = await queryDatabaseForApplicantOfferTotals(params);
  const applicationTotals = await queryDatabaseForApplicationTotals(params);

  const combinedTotals = {
    offerTotals,
    applicationTotals,
  };

  return { totals: combinedTotals };
}

async function queryDatabaseForApplicationTotals(params: Partial<IGetAllDashboardRowsParams>) {
  // Application_summary table does not have a tag column
  // it is filtered out with getPsqlFilters()
  // though we have to return nothing, as if the filter does not match any records
  if ('tag' in params.filters) return [] as ApplicationStatusTotals;

  const applicationFilters = params.filters ? getPsqlFilters(params.filters, ApplicationPGFilterMap) : null;

  const tagsMatchArray: string[] = params.tagsMatch instanceof Array ? params.tagsMatch : [params.tagsMatch];

  const qp = new URLSearchParams();

  if (params.uni) qp.set('uni', params.uni);
  if (params.intakeId) qp.set('intakeId', params.intakeId);
  if (params.search) qp.set('search', params.search);
  if (params.startDate) qp.set('startDate', params.startDate);
  if (params.endDate) qp.set('endDate', params.endDate);

  if (applicationFilters) qp.set('filters', JSON.stringify(applicationFilters)); // type ApplicantOfferPGFilterMap

  if (params.agentId) qp.set('agentId', params.agentId);
  if (params.officeId) qp.set('officeId', params.officeId);
  if (params.currentUserRegionId) qp.set('regionId', params.currentUserRegionId);
  if (params.tagsMatch) qp.set('tagsMatch', tagsMatchArray.join(','));
  if (params.idleFrom?.length) qp.set('idleFrom', String(params.idleFrom[0]));
  if (params.idleTo?.length) qp.set('idleTo', String(params.idleTo[0]));

  const response: GetApplicationsStageTotalsResponse | ErrorResponseBody = await queryData({
    uri: `/apply/list/applications/totals`,
    method: Method.Get,
    qp,
    idToken: params.idToken,
  });

  if ('error' in response) {
    throw new Error(JSON.stringify(response.error));
  }

  return response.totals;
}

async function queryDatabaseForApplicantOfferTotals(params: Partial<IGetAllDashboardRowsParams>) {
  const applicantOfferFilters = params.filters ? getPsqlFilters(params.filters, ApplicantOfferPGFilterMap) : null;

  const tagsMatchArray: string[] = params.tagsMatch instanceof Array ? params.tagsMatch : [params.tagsMatch];

  const qp = new URLSearchParams();

  if (params.uni) qp.set('uni', params.uni);
  if (params.intakeId) qp.set('intakeId', params.intakeId);
  if (params.search) qp.set('search', params.search);
  if (params.startDate) qp.set('startDate', params.startDate);
  if (params.endDate) qp.set('endDate', params.endDate);

  if (applicantOfferFilters) qp.set('filters', JSON.stringify(applicantOfferFilters)); // type ApplicantOfferPGFilterMap

  if (params.agentId) qp.set('agentId', params.agentId);
  if (params.officeId) qp.set('officeId', params.officeId);
  if (params.currentUserRegionId) qp.set('regionId', params.currentUserRegionId);
  if (params.tagsMatch) qp.set('tagsMatch', tagsMatchArray.join(','));
  if (params.idleFrom?.length) qp.set('idleFrom', String(params.idleFrom[0]));
  if (params.idleTo?.length) qp.set('idleTo', String(params.idleTo[0]));

  const response: GetOffersStageTotalsResponse | ErrorResponseBody = await queryData({
    uri: `/apply/list/offers/totals`,
    method: Method.Get,
    qp,
    idToken: params.idToken,
  });

  if ('error' in response) {
    throw new Error(JSON.stringify(response.error));
  }

  return response.totals;
}

export async function queryDatabaseForApplications(
  params: IGetAllDashboardRowsParams,
  stages: string[],
): Promise<IApplicationSummaryTableRow[] | undefined> {
  if (!stages || stages.length === 0) return undefined;

  // Application_summary table does not have a tag column
  // it is filtered out with getPsqlFilters()
  // though we have to return nothing, as if the filter does not match any records
  if ('tag' in params.filters) return [];

  const applicationFilters = params.filters ? getPsqlFilters(params.filters, ApplicationPGFilterMap) : null;

  const tagsMatchArray: string[] = params.tagsMatch instanceof Array ? params.tagsMatch : [params.tagsMatch];

  const qp = new URLSearchParams();

  qp.set('applicationStage', stages.join(','));

  if (params.uni) qp.set('uni', params.uni);
  if (params.intakeId) qp.set('intakeId', params.intakeId);
  if (params.page) qp.set('page', String(params.page));
  if (params.pageSize) qp.set('pageSize', String(params.pageSize));
  if (params.sortCol) qp.set('sortCol', params.sortCol);
  if (params.sortDir) qp.set('sortDir', params.sortDir);
  if (params.search) qp.set('search', params.search);
  if (params.startDate) qp.set('startDate', params.startDate);
  if (params.endDate) qp.set('endDate', params.endDate);

  if (applicationFilters) qp.set('filters', JSON.stringify(applicationFilters)); // type ApplicantOfferPGFilterMap

  if (params.agentId) qp.set('agentId', params.agentId);
  if (params.officeId) qp.set('officeId', params.officeId);
  if (params.currentUserRegionId) qp.set('regionId', params.currentUserRegionId);
  if (params.tagsMatch) qp.set('tagsMatch', tagsMatchArray.join(','));
  if (params.idleFrom?.length) qp.set('idleFrom', String(params.idleFrom[0]));
  if (params.idleTo?.length) qp.set('idleTo', String(params.idleTo[0]));

  const response: GetApplicationsSummaryResponse | ErrorResponseBody = await queryData({
    uri: `/apply/list/applications`,
    method: Method.Get,
    qp,
    idToken: params.idToken,
  });

  if ('error' in response) {
    throw new Error(JSON.stringify(response.error));
  }

  return response.applications;
}

export async function queryDatabaseForApplicantOffers(
  params: IGetAllDashboardRowsParams,
  stages: string[],
): Promise<IApplicantOfferSummaryTableRow[] | undefined> {
  if (!stages || stages.length === 0) return undefined;

  const offerFilters = params.filters ? getPsqlFilters(params.filters, ApplicantOfferPGFilterMap) : null;

  const tagsMatchArray: string[] = params.tagsMatch instanceof Array ? params.tagsMatch : [params.tagsMatch];

  const qp = new URLSearchParams();

  qp.set('offerStage', stages.join(','));

  if (params.uni) qp.set('uni', params.uni);
  if (params.intakeId) qp.set('intakeId', params.intakeId);
  if (params.page) qp.set('page', String(params.page));
  if (params.pageSize) qp.set('pageSize', String(params.pageSize));
  if (params.sortCol) qp.set('sortCol', params.sortCol);
  if (params.sortDir) qp.set('sortDir', params.sortDir);
  if (params.search) qp.set('search', params.search);
  if (params.startDate) qp.set('startDate', params.startDate);
  if (params.endDate) qp.set('endDate', params.endDate);

  if (offerFilters) qp.set('filters', JSON.stringify(offerFilters)); // type ApplicantOfferPGFilterMap

  if (params.agentId) qp.set('agentId', params.agentId);
  if (params.officeId) qp.set('officeId', params.officeId);
  if (params.currentUserRegionId) qp.set('regionId', params.currentUserRegionId);
  if (params.tagsMatch) qp.set('tagsMatch', tagsMatchArray.join(','));
  if (params.idleFrom?.length) qp.set('idleFrom', String(params.idleFrom[0]));
  if (params.idleTo?.length) qp.set('idleTo', String(params.idleTo[0]));

  const response: GetOffersSummaryResponse | ErrorResponseBody = await queryData({
    uri: `/apply/list/offers`,
    method: Method.Get,
    qp,
    idToken: params.idToken,
  });

  if ('error' in response) {
    throw new Error(JSON.stringify(response.error));
  }

  return response.offers;
}

export async function getAllApplyCourses(params: IGetAllParams) {
  const { uni, idToken, intakeId, startDate, endDate, agentId, officeId, currentUserRegionId } = params;

  const qp = new URLSearchParams();

  if (uni) qp.set('uni', uni);
  if (intakeId) qp.set('intakeId', intakeId);
  if (startDate) qp.set('startDate', startDate);
  if (endDate) qp.set('endDate', endDate);
  if (agentId) qp.set('agentId', agentId);
  if (officeId) qp.set('officeId', officeId);
  if (currentUserRegionId) qp.set('currentUserRegionId', currentUserRegionId);

  const applyCoursesResponse: IGetAllApplyCourseResponse | ErrorResponseBody = await queryData({
    uri: `/apply/list/course`,
    method: Method.Get,
    qp,
    idToken,
  });

  if ('error' in applyCoursesResponse) {
    console.warn('Error in getAllApplyCourses.');
    throw new Error(JSON.stringify(applyCoursesResponse.error));
  }

  return {
    courses: applyCoursesResponse.data,
  };
}

export async function getApplyUniConfig(params: { idToken: string; uni: string }) {
  const { uni, idToken } = params;

  if (!uni) {
    throw new Error('IntakeId or uni not provided to api request.');
  }

  const qp = new URLSearchParams();
  if (uni) qp.set('uni', uni);

  const applyUniConfigResponse: GetApplyConfigResponse | ErrorResponseBody = await queryData({
    uri: `/apply/config`,
    method: Method.Get,
    qp,
    idToken,
  });

  if ('error' in applyUniConfigResponse) {
    console.warn('Error in applyUniConfigResponse.');
    throw new Error(JSON.stringify(applyUniConfigResponse.error));
  }

  // we substitute missing entries in applyUniConfigResponse with defaultApplyUniConfig.
  // incomplete apply configs should maintain functionality in this way.
  const config = _.merge({}, defaultApplyUniConfig, applyUniConfigResponse.config);

  return {
    config,
  };
}
/**
 * Registers a new applicant through an agent
 *
 * @param idToken Authentication token
 * @param universityId University ID
 * @param email Applicant email
 * @param preferredName Applicant preferred name
 * @param nationality Applicant nationality
 * @param dob Date of birth
 * @param phoneNumber Phone number
 * @param feeStatus Selected fee status
 * @param programmeChoices Array of programme choices
 * @param agentConsent Agent consent
 * @returns Response from the API
 */
export const agentRegisterApplicant = async (
  idToken: string,
  universityId: string,
  email: string,
  preferredName: string,
  nationality: string,
  dob: string,
  phoneNumber: string,
  feeStatus: string,
  programmeChoices: IProgrammeChoice[],
  agentConsent: boolean,
): Promise<IAgentRegistrationResponse | ErrorResponseBody> => {
  return queryData({
    uri: `/apply/agent/register`,
    method: Method.Post,
    idToken,
    body: {
      universityId,
      email,
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      programmeChoices,
      agentConsent,
    },
  });
};

export const uniStaffRegisterApplicant = async (
  idToken: string,
  universityId: string,
  email: string,
  preferredName: string,
  nationality: string,
  dob: string,
  phoneNumber: string,
  feeStatus: string,
  disableEmailNotifications: boolean,
  programmeChoices: IProgrammeChoice[],
): Promise<IUniStaffRegistrationResponse | ErrorResponseBody> => {
  return queryData({
    uri: `/apply/staff/register`,
    method: Method.Post,
    idToken,
    body: {
      universityId,
      email,
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      disableEmailNotifications,
      programmeChoices,
    },
  });
};

export const partnerRegisterApplicant = async (
  idToken: string,
  universityId: string,
  email: string,
  preferredName: string,
  nationality: string,
  dob: string,
  phoneNumber: string,
  feeStatus: string,
  programmeChoices: IProgrammeChoice[],
): Promise<IPartnerRegistrationResponse | ErrorResponseBody> => {
  return queryData({
    uri: `/apply/partner/register`,
    method: Method.Post,
    idToken,
    body: {
      universityId,
      email,
      preferredName,
      nationality,
      dob,
      phoneNumber,
      feeStatus,
      programmeChoices,
    },
  });
};

export const applyStudentsPinTag = async (
  idToken: string,
  uni: string,
  offerId: string,
  applicantId: string,
  tag: { id: string },
): Promise<any> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/tags/pin`,
    method: Method.Put,
    body: { tag },
    idToken,
  });
};

export const applyStudentsUnpinTag = async (
  idToken: string,
  uni: string,
  offerId: string,
  applicantId: string,
  tag: { id: string },
): Promise<any> => {
  return queryData({
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/offers/${offerId}/tags/unpin`,
    method: Method.Put,
    body: { tag },
    idToken,
  });
};

export const updateApplicantDetails = async (
  idToken: string,
  applicantId: string,
  uni: string,
  applicationId: string,
  data: IApplicantData,
): Promise<any | ErrorResponseBody> => {
  return queryData({
    method: Method.Put,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/application/${applicationId}/applicant-details`,
    idToken,
    body: { ...data },
  });
};

export const unlinkApplicantEmail = async (
  idToken: string,
  uni: string,
  applicantId: string,
): Promise<any | ErrorResponseBody> => {
  return queryData({
    method: Method.Delete,
    domain: await getApiUrl(),
    uri: `/apply/${uni}/applicant/${applicantId}/unlink-email`,
    idToken,
  });
};

export const saveDocumentWithFiles = async (
  idToken: string,
  uni: string,
  applicantId: string,
  docType: string,
  fileNames: string[],
  s3Keys: string[],
  docKey: string,
): Promise<any> => {
  const normalizedBasePath = docType.startsWith('/') ? `${docType}` : `/${docType}`;

  const uri = `/documents/${uni}/applicant/${applicantId}${normalizedBasePath}/${docKey}`;

  const body = {
    fileNames,
    s3Keys,
    label: docType,
    status: 'UPLOADED_OK',
  };

  return queryData({
    body,
    idToken,
    uri,
    method: Method.Post,
  });
};

export const updateRequirementDocument = async (
  idToken: string,
  uni: string,
  applicantId: string,
  offerId: string,
  requirementId: string,
  body: IDocumentFulfilmentData,
): Promise<any> => {
  const uri = `/apply/agent/${uni}/applicant/${applicantId}/offers/${offerId}/requirements/${requirementId}`;

  return queryData({
    body,
    idToken,
    uri,
    method: Method.Put,
  });
};

export type UploadConfig = {
  fileName: string;
  fileType: string;
  docKey: string;
  docType: DocumentType;
};

export type UploadResult = {
  url: string;
  s3Key: string;
  fileName: string;
};

export type UploadFnParams = {
  file: Blob;
  idToken: string;
  university: string;
  applicantId: string;
  uploadConf: UploadConfig;
  onProgress?: (progress: number) => void;
  signal?: AbortSignal;
};

export const getStaffInternalUploadUrl = async ({
  idToken,
  university,
  applicantId,
  uploadConf,
}: {
  idToken: string;
  university: string;
  applicantId: string;
  uploadConf: UploadConfig;
}): Promise<DocumentPresignedUrlResponse | ErrorResponseBody> => {
  if (!uploadConf.docKey) {
    throw new Error('docKey is required for staff document upload');
  }

  const response = await ApiClient.DocumentManager.Applicant.Staff.UploadUrl.post({
    pathParams: { uni: university, applicantId },
    body: {
      docType: DocumentType.StaffDocument,
      fileName: uploadConf.fileName,
      fileType: uploadConf.fileType,
      docKey: uploadConf.docKey,
    },
    options: { idToken },
  });
  if (!response.data) throw response.error;
  return response.data as DocumentPresignedUrlResponse | ErrorResponseBody;
};

export const uploadStaffInternalFileWithProgress = async ({
  file,
  idToken,
  university,
  applicantId,
  uploadConf,
  onProgress,
  signal,
}: UploadFnParams): Promise<UploadResult> => {
  const uploadUrlResponse = await getStaffInternalUploadUrl({
    idToken,
    university,
    applicantId,
    uploadConf,
  });

  if (isErrorResponse(uploadUrlResponse)) {
    throw new Error('Failed to get upload URL');
  }

  const { presignedUrl, s3Key, fileName } = uploadUrlResponse;
  return uploadFileToPresignedUrl({ presignedUrl, s3Key, fileName, file, onProgress, signal });
};

export const updateRequirementInternalData = async ({
  idToken,
  uni,
  applicantId,
  offerId,
  requirementId,
  body,
}: {
  idToken: string;
  uni: string;
  applicantId: string;
  offerId: string;
  requirementId: string;
  body: { s3Keys: string[]; fileNames?: string[] };
}): Promise<UpdateOfferRequirementInternalDataResponse | ErrorResponseBody> => {
  const response = await ApiClient.Apply.Uni.Offer.Requirement.InternalData.update({
    pathParams: { uni, applicantId, offerId, requirementId },
    body,
    options: { idToken },
  });
  if (!response.data) throw response.error;
  return response.data as UpdateOfferRequirementInternalDataResponse | ErrorResponseBody;
};

export const deleteStaffInternalDocument = async ({
  idToken,
  uni,
  applicantId,
  docKey,
  s3Key,
}: {
  idToken: string;
  uni: string;
  applicantId: string;
  docKey: string;
  s3Key: string;
}): Promise<DeleteStaffInternalDocumentResponse | ErrorResponseBody> => {
  const response = await ApiClient.DocumentManager.Applicant.Staff.delete({
    pathParams: { uni, applicantId, docKey },
    queryParams: { s3Key },
    options: { idToken },
  });
  if (!response.data) throw response.error;
  return response.data as DeleteStaffInternalDocumentResponse | ErrorResponseBody;
};

export const getApplicantDocuments = async (
  idToken: string,
  uni: string,
  applicantId: string,
  docType: string,
): Promise<any> => {
  const normalizedBasePath = docType.startsWith('/') ? `${docType}` : `/${docType}`;
  const uri = `/documents/${uni}/applicant/${applicantId}${normalizedBasePath}`;

  return queryData({
    idToken,
    uri,
    method: Method.Get,
  });
};

export const deleteDocumentFile = async (
  idToken: string,
  uni: string,
  applicantId: string,
  docType: string,
  docKey: string,
): Promise<any> => {
  const normalizedBasePath = docType.startsWith('/') ? `${docType}` : `/${docType}`;
  const uri = `/documents/${uni}/applicant/${applicantId}${normalizedBasePath}/${docKey}`;

  return queryData({
    idToken,
    uri,
    method: Method.Delete,
  });
};

export const updateDocumentFiles = async (
  idToken: string,
  uni: string,
  applicantId: string,
  docType: string,
  docKey: string,
  updatedDocument: any,
): Promise<any> => {
  const normalizedBasePath = docType.startsWith('/') ? `${docType}` : `/${docType}`;
  const uri = `/documents/${uni}/applicant/${applicantId}${normalizedBasePath}/${docKey}`;

  return queryData({
    body: updatedDocument,
    idToken,
    uri,
    method: Method.Post,
  });
};

export const updateOfferProofOfPayment = async (
  idToken: string,
  uni: string,
  applicantId: string,
  offerId: string,
  updatedDocument: any,
): Promise<any> => {
  const uri = `/apply/agent/${uni}/applicant/${applicantId}/offers/${offerId}/proof-of-payment`;
  return queryData({
    body: updatedDocument,
    idToken,
    uri,
    method: Method.Put,
  });
};

export const getUniList = async (idToken: string): Promise<any> => {
  const uri = '/apply/uni-list';
  return queryData({
    uri,
    method: Method.Get,
    idToken,
  });
};

export type { SchedulingInfoResponse as CalendlySchedulingInfoResponse };

export const getCalendlySchedulingInfo = async (
  idToken: string,
  uni: string,
  eventType: string,
): Promise<SchedulingInfoResponse> => {
  const response = await ApiClient.Apply.CalendlyInterview.SchedulingInfo.get({
    pathParams: { uni, eventTypeUuid: eventType },
    options: { idToken },
  });

  if (response.data) return response.data as SchedulingInfoResponse;
  throw response.error;
};
